import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { DateField, SelectField, TextField } from 'components/ui';
import {
  PICKER_ALL_STATES,
  PICKER_ETHNICITY,
  PICKER_GRADE,
  PICKER_LANGUAGES,
  PICKER_SCLA_HEAR_ABOUT_US,
  PICKER_YES_NO,
} from 'constants/picker';
import updateSclaAppBackground from 'features/scla/api/updateSclaAppBackground';
import {
  emailError,
  formatPhone,
  isValidEmail,
  isValidPhone,
  isValidZip,
  phoneDigits,
  phoneError,
  zipError,
} from 'features/scla/utils/contactFields';
import useUserStore from 'store/useUserStore';
import { getSclaAppFamilyPath } from 'utils/navigation';
import ChoiceChips from '../components/steps/ChoiceChips';
import SclaStepCard from '../components/steps/SclaStepCard';
import styles from '../components/steps/SclaStep.module.scss';
import { SCLA_TRANSPORTATION } from '../config/applicationOptions';
import useAutosave, { useSectionReady } from '../hooks/useAutosave';
import useSclaSectionPath from '../hooks/useSclaSectionPath';
import { SclaApplicationOutletContext } from './SclaAppLayout';
import clearFilledErrors from '../utils/clearFilledErrors';

type Option = { label: string; value: string };

type BackgroundForm = {
  firstName: string;
  lastName: string;
  email: string;
  dob: Date | null;
  primaryLanguage: string;
  primaryLanguageOther: string;
  gradeLevel: string;
  ethnicity: string;
  ethnicityOther: string;
  instagramHandle: string;
  studentPhoneNumber: string;
  parentPhoneNumber: string;
  streetAddress: string;
  cityAddress: string;
  stateAddress: string;
  zipAddress: string;
  appliedBefore: string;
  hearAboutUs: string;
  hearAboutUsOther: string;
  recommendedBy: string;
  transportation: string;
};

function asOptions(items: { label: string; value: string }[]): Option[] {
  return items.map(item => ({ label: item.label, value: item.value }));
}

const GRADE_OPTIONS: Option[] = [
  { label: '6th Grade', value: '6' },
  { label: '7th Grade', value: '7' },
  ...PICKER_GRADE.map(item => ({ label: item.label, value: item.value })),
];

function normalizeGrade(value: string): string {
  const match = value.trim().match(/^(\d{1,2})/);
  return match ? match[1] : value.trim();
}

function gradeChoices(stored: string): Option[] {
  const value = normalizeGrade(stored);
  if (value && !GRADE_OPTIONS.some(option => option.value === value)) {
    return [{ label: stored.trim() || value, value }, ...GRADE_OPTIONS];
  }
  return GRADE_OPTIONS;
}

function usableDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function dobMessage(dob: Date | null): string {
  if (!dob || Number.isNaN(dob.getTime())) return 'Enter your date of birth.';
  const today = new Date();
  const born = new Date(dob.getFullYear(), dob.getMonth(), dob.getDate());
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (born.getTime() > todayStart.getTime()) return 'Date of birth cannot be in the future.';
  let age = todayStart.getFullYear() - born.getFullYear();
  const month = todayStart.getMonth() - born.getMonth();
  if (month < 0 || (month === 0 && todayStart.getDate() < born.getDate())) age -= 1;
  if (age < 10 || age > 20) return 'Enter an age between 10 and 20.';
  return '';
}

function withCurrent(options: Option[], value: string): Option[] {
  if (!value || options.some(option => option.value === value)) return options;
  return [{ label: value, value }, ...options];
}

function text(value: string | null | undefined) {
  return value || '';
}

function isListed(value: string, options: Option[]) {
  return options.some(option => option.value.toLocaleLowerCase() === value.toLocaleLowerCase());
}

function mapForm(application: SclaApplicationOutletContext['application'], accountEmail: string, accountFirst: string, accountLast: string): BackgroundForm {
  const background = application.backgroundInfo;
  const ethnicityOptions = asOptions(PICKER_ETHNICITY);
  const languageOptions = asOptions(PICKER_LANGUAGES);
  const hearOptions = asOptions(PICKER_SCLA_HEAR_ABOUT_US);
  const ethnicity = text(background.ethnicity);
  const language = text(background.primaryLanguage);
  const hear = text(background.hearAboutUs);
  return {
    firstName: text(background.firstName) || accountFirst,
    lastName: text(background.lastName) || accountLast,
    email: text(background.email) || accountEmail,
    dob: usableDate(background.dob),
    primaryLanguage: language && !isListed(language, languageOptions.filter(item => item.value !== 'other'))
      ? 'other'
      : language,
    primaryLanguageOther: language && !isListed(language, languageOptions.filter(item => item.value !== 'other'))
      ? language
      : '',
    gradeLevel: normalizeGrade(text(background.gradeLevel)),
    ethnicity: ethnicity && !isListed(ethnicity, ethnicityOptions.filter(item => item.value !== 'other'))
      ? 'other'
      : ethnicity,
    ethnicityOther: ethnicity && !isListed(ethnicity, ethnicityOptions.filter(item => item.value !== 'other'))
      ? ethnicity
      : '',
    instagramHandle: text(background.instagramHandle),
    studentPhoneNumber: formatPhone(text(background.studentPhoneNumber)),
    parentPhoneNumber: formatPhone(text(background.parentPhoneNumber)),
    streetAddress: text(background.streetAddress),
    cityAddress: text(background.cityAddress),
    stateAddress: text(background.stateAddress),
    zipAddress: text(background.zipAddress),
    appliedBefore: text(background.appliedBefore),
    hearAboutUs: hear && !isListed(hear, hearOptions.filter(item => item.value !== 'other'))
      ? 'other'
      : hear,
    hearAboutUsOther: hear && !isListed(hear, hearOptions.filter(item => item.value !== 'other'))
      ? hear
      : '',
    recommendedBy: text(background.recommendedBy),
    transportation: text(background.transportation),
  };
}

export default function SclaApplicationBackground() {
  const { application, setApplication, readOnly } =
    useOutletContext<SclaApplicationOutletContext>();
  const user = useUserStore(state => state.user);
  const sectionPath = useSclaSectionPath();
  const navigate = useNavigate();
  const [form, setForm] = useState(() =>
    mapForm(application, user?.email || '', user?.firstName || '', user?.lastName || ''),
  );
  const ready = useSectionReady(application._id, () => {
    setForm(mapForm(application, user?.email || '', user?.firstName || '', user?.lastName || ''));
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dobInvalid, setDobInvalid] = useState(false);
  const lastValid = useRef({
    email: isValidEmail(form.email) ? form.email.trim() : '',
    studentPhone: isValidPhone(form.studentPhoneNumber) ? formatPhone(form.studentPhoneNumber) : '',
    parentPhone: isValidPhone(form.parentPhoneNumber) ? formatPhone(form.parentPhoneNumber) : '',
    zip: isValidZip(form.zipAddress) ? form.zipAddress.trim() : '',
  });

  const patch = (partial: Partial<BackgroundForm>) => {
    setForm(current => ({ ...current, ...partial }));
    setErrors(current => clearFilledErrors(current, partial, { ethnicityOther: 'ethnicity', primaryLanguageOther: 'primaryLanguage', hearAboutUsOther: 'hearAboutUs' }));
  };

  const serialized = JSON.stringify({
    ...form,
    dob: usableDate(form.dob)?.toISOString() ?? '',
  });

  const save = async () => {
    const dob = dobInvalid ? null : usableDate(form.dob);
    const ethnicity = form.ethnicity === 'other' ? form.ethnicityOther : form.ethnicity;
    const primaryLanguage = form.primaryLanguage === 'other'
      ? form.primaryLanguageOther
      : form.primaryLanguage;
    const hearAboutUs = form.hearAboutUs === 'other' ? form.hearAboutUsOther : form.hearAboutUs;
    const response = await updateSclaAppBackground(application._id, {
      firstName: form.firstName,
      lastName: form.lastName,
      name: `${form.firstName} ${form.lastName}`.trim(),
      email: lastValid.current.email,
      studentPhoneNumber: lastValid.current.studentPhone,
      parentPhoneNumber: lastValid.current.parentPhone,
      streetAddress: form.streetAddress,
      cityAddress: form.cityAddress,
      stateAddress: form.stateAddress,
      zipAddress: lastValid.current.zip,
      ...(dob ? { dob } : {}),
      instagramHandle: form.instagramHandle,
      ethnicity,
      gradeLevel: form.gradeLevel,
      primaryLanguage,
      transportation: form.transportation,
      appliedBefore: form.appliedBefore,
      hearAboutUs,
      recommendedBy: form.hearAboutUs === 'Recommended by TXT Student' ? form.recommendedBy : '',
    });
    setApplication(response);
    return true;
  };

  const { flush } = useAutosave({
    serialized,
    enabled: !readOnly && ready,
    save,
  });

  const languageOptions = useMemo(
    () => withCurrent(asOptions(PICKER_LANGUAGES), form.primaryLanguage),
    [form.primaryLanguage],
  );
  const ethnicityOptions = useMemo(
    () => withCurrent(asOptions(PICKER_ETHNICITY), form.ethnicity),
    [form.ethnicity],
  );
  const stateOptions = useMemo(
    () => withCurrent(asOptions(PICKER_ALL_STATES), form.stateAddress),
    [form.stateAddress],
  );

  const handleContinue = async () => {
    const posted = await flush();
    const ethnicity = form.ethnicity === 'other' ? form.ethnicityOther : form.ethnicity;
    const primaryLanguage = form.primaryLanguage === 'other'
      ? form.primaryLanguageOther
      : form.primaryLanguage;
    const hearAboutUs = form.hearAboutUs === 'other' ? form.hearAboutUsOther : form.hearAboutUs;
    const nextErrors: Record<string, string> = {};
    const emailMessage = emailError(form.email);
    const studentPhoneMessage = phoneError(form.studentPhoneNumber, false);
    const parentPhoneMessage = phoneError(form.parentPhoneNumber, true);
    const zipMessage = zipError(form.zipAddress);
    if (!form.firstName) nextErrors.firstName = 'Enter your first name.';
    if (!form.lastName) nextErrors.lastName = 'Enter your last name.';
    if (emailMessage) nextErrors.email = emailMessage;
    if (dobInvalid || dobMessage(form.dob)) {
      nextErrors.dob = dobInvalid ? 'Enter a valid date of birth.' : dobMessage(form.dob);
    }
    if (studentPhoneMessage) nextErrors.studentPhoneNumber = studentPhoneMessage;
    if (parentPhoneMessage) nextErrors.parentPhoneNumber = parentPhoneMessage;
    if (!form.streetAddress) nextErrors.streetAddress = 'Enter your street address.';
    if (!form.cityAddress) nextErrors.cityAddress = 'Enter your city.';
    if (!form.stateAddress) nextErrors.stateAddress = 'Choose your state.';
    if (zipMessage) nextErrors.zipAddress = zipMessage;
    if (!primaryLanguage) nextErrors.primaryLanguage = 'Enter the language spoken at home.';
    if (!form.gradeLevel) nextErrors.gradeLevel = 'Choose your grade.';
    if (!ethnicity) nextErrors.ethnicity = 'Choose your ethnicity.';
    if (!form.appliedBefore) nextErrors.appliedBefore = 'Choose yes or no.';
    if (!hearAboutUs) nextErrors.hearAboutUs = 'Choose how you heard about SCLA.';
    if (!form.transportation) nextErrors.transportation = 'Choose how you get around.';
    setErrors(nextErrors);
    const ok = posted && Object.keys(nextErrors).length === 0;
    if (ok) navigate(sectionPath(getSclaAppFamilyPath()));
    return ok;
  };

  const transportOptions = form.transportation
    && !SCLA_TRANSPORTATION.some(option => option.value === form.transportation)
    ? [{ label: form.transportation, value: form.transportation }, ...SCLA_TRANSPORTATION]
    : [...SCLA_TRANSPORTATION];

  return (
    <SclaStepCard
      title="Background"
      intro="Basic info so we can reach you. Most answers are a tap."
      onContinue={handleContinue}
      continueLabel="Save and continue →"
      readOnly={readOnly}
    >
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>You</legend>
        <div className={styles.grid}>
          <TextField id="scla-first" label="First name" required value={form.firstName} error={errors.firstName} onChange={value => patch({ firstName: value })} />
          <TextField id="scla-last" label="Last name" required value={form.lastName} error={errors.lastName} onChange={value => patch({ lastName: value })} />
          <DateField
            id="scla-dob"
            label="Date of birth"
            required
            value={usableDate(form.dob)}
            error={errors.dob}
            onInvalid={() => {
              setDobInvalid(true);
              setErrors(current => ({ ...current, dob: 'Enter a valid date of birth.' }));
            }}
            onChange={value => {
              setDobInvalid(false);
              patch({ dob: value });
              setErrors(current => ({ ...current, dob: value ? dobMessage(value) : '' }));
            }}
          />
          <SelectField id="scla-language" label="Primary language at home" required value={form.primaryLanguage || null} options={languageOptions} error={errors.primaryLanguage} onChange={value => patch({ primaryLanguage: value || '' })} />
          <div className={styles.span}>
            <ChoiceChips id="scla-grade" label="Grade level" required error={errors.gradeLevel} options={gradeChoices(form.gradeLevel)} value={normalizeGrade(form.gradeLevel)} disabled={readOnly} onChange={value => patch({ gradeLevel: value })} />
          </div>
          <SelectField id="scla-ethnicity" label="Ethnicity" required value={form.ethnicity || null} options={ethnicityOptions} error={errors.ethnicity} onChange={value => patch({ ethnicity: value || '' })} />
          {form.ethnicity === 'other' ? (
            <TextField id="scla-ethnicity-other" label="Enter your ethnicity" required value={form.ethnicityOther} error={errors.ethnicity} onChange={value => patch({ ethnicityOther: value })} />
          ) : null}
          {form.primaryLanguage === 'other' ? (
            <TextField id="scla-language-other" label="Enter your language" required value={form.primaryLanguageOther} error={errors.primaryLanguage} onChange={value => patch({ primaryLanguageOther: value })} />
          ) : null}
        </div>
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>Contact</legend>
        <div className={styles.grid}>
          <TextField
            id="scla-email"
            label="Email"
            type="email"
            required
            value={form.email}
            error={errors.email}
            onChange={value => {
              patch({ email: value });
              setErrors(current => ({
                ...current,
                email: value.trim() && !isValidEmail(value) ? 'Enter a valid email.' : '',
              }));
              if (isValidEmail(value)) lastValid.current.email = value.trim();
            }}
          />
          <TextField id="scla-instagram" label="Instagram" value={form.instagramHandle} placeholder="Optional, @handle" onChange={value => patch({ instagramHandle: value })} />
          <TextField
            id="scla-phone"
            label="Your phone"
            type="tel"
            value={form.studentPhoneNumber}
            error={errors.studentPhoneNumber}
            onChange={value => {
              const formatted = formatPhone(value);
              patch({ studentPhoneNumber: formatted });
              setErrors(current => ({
                ...current,
                studentPhoneNumber: phoneError(formatted, false),
              }));
              if (!phoneDigits(formatted)) lastValid.current.studentPhone = '';
              else if (isValidPhone(formatted)) lastValid.current.studentPhone = formatted;
            }}
          />
          <TextField
            id="scla-parent-phone"
            label="Parent/guardian phone"
            type="tel"
            required
            value={form.parentPhoneNumber}
            error={errors.parentPhoneNumber}
            onChange={value => {
              const formatted = formatPhone(value);
              patch({ parentPhoneNumber: formatted });
              setErrors(current => ({
                ...current,
                parentPhoneNumber: phoneDigits(formatted) ? phoneError(formatted, true) : '',
              }));
              if (isValidPhone(formatted)) lastValid.current.parentPhone = formatted;
            }}
          />
        </div>
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>Address</legend>
        <div className={styles.grid}>
          <div className={styles.span}>
            <TextField id="scla-street" label="Street address" required value={form.streetAddress} error={errors.streetAddress} onChange={value => patch({ streetAddress: value })} />
          </div>
          <div className={`${styles.span} ${styles.addressRow}`}>
            <TextField id="scla-city" label="City" required value={form.cityAddress} error={errors.cityAddress} onChange={value => patch({ cityAddress: value })} />
            <SelectField id="scla-state" label="State" required searchable value={form.stateAddress || null} options={stateOptions} error={errors.stateAddress} onChange={value => patch({ stateAddress: value || '' })} />
            <TextField
              id="scla-zip"
              label="Zip"
              required
              value={form.zipAddress}
              error={errors.zipAddress}
              onChange={value => {
                patch({ zipAddress: value });
                setErrors(current => ({
                  ...current,
                  zipAddress: value.trim() && zipError(value) ? zipError(value) : '',
                }));
                if (isValidZip(value)) lastValid.current.zip = value.trim();
              }}
            />
          </div>
        </div>
      </fieldset>
      <fieldset className={styles.group} disabled={readOnly}>
        <legend>About applying</legend>
        <div className={styles.grid}>
          <div className={styles.span}>
            <ChoiceChips id="scla-applied" label="Have you applied to SCLA before?" required error={errors.appliedBefore} options={asOptions(PICKER_YES_NO)} value={form.appliedBefore} disabled={readOnly} onChange={value => patch({ appliedBefore: value })} />
          </div>
          <div className={styles.span}>
            <ChoiceChips id="scla-hear" label="How did you hear about SCLA?" required error={errors.hearAboutUs} options={asOptions(PICKER_SCLA_HEAR_ABOUT_US)} value={form.hearAboutUs} disabled={readOnly} onChange={value => patch({ hearAboutUs: value })} />
          </div>
          {form.hearAboutUs === 'other' ? (
            <TextField id="scla-hear-other" label="Other" required value={form.hearAboutUsOther} error={errors.hearAboutUs} onChange={value => patch({ hearAboutUsOther: value })} />
          ) : null}
          {form.hearAboutUs === 'Recommended by TXT Student' ? (
            <TextField id="scla-recommended" label="Who recommended you?" required value={form.recommendedBy} onChange={value => patch({ recommendedBy: value })} />
          ) : null}
          <div className={styles.span}>
            <ChoiceChips
              id="scla-transport"
              label="How do you usually get around?"
              required
              error={errors.transportation}
              variant="cards"
              options={transportOptions}
              value={form.transportation}
              disabled={readOnly}
              onChange={value => patch({ transportation: value })}
            />
          </div>
        </div>
      </fieldset>
    </SclaStepCard>
  );
}
